/**
 * BannerEditPage - Redesigned Banner Edit Page
 *
 * Modern, professional edit page using EditPageLayout with:
 * - Tab-based navigation (Basic Info, Display Settings)
 * - Consistent form field styling
 * - Collapsible sections
 * - Keyboard shortcuts
 * - Unsaved changes detection
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import {
	BaseForm,
	Button,
	NumberBox,
	SwitchBox,
	TextAreaBox,
	TextBox,
} from '../../../components/base';
import { DxDateBox } from '../../../components/base';
import { ImageBox } from '../../../components/base/ImageBox';
import { EditPageLayout, EditPageSection, EditPageField } from '../../../components/container';
import { eBaseActionStatus } from '../../../context/base/eBaseActionStatus';
import { BaseShowDetail } from '../../../context/base/BaseShowDetail';
import { BannerModel, dfBannerModel, validBannerSchema } from '../../../context/models/banners/BannerModel';
import { BannerActionTypes } from '../../../context/stores/banner/IBannerActionsTypes';
import { actions } from '../../../context/stores/rootActions';
import { AppState } from '../../../context/stores/rootReducers';
import { history } from '../../../utils/history';
import BannerConfigTable from '../../banner-config';
import { Image as ImageIcon, Link as LinkIcon, Calendar, Settings, FileText } from 'lucide-react';

const mapStateToProps = (state: AppState) => {
	return {
		status: state.banner.status,
		allBanners: state.banner.allBanners,
		detailResponse: state.banner.detailResponse,
		selectedIds: state.banner.selectedIds,
		showDetail: state.banner.showDetail,
		showConfirm: state.banner.showConfirm,
	};
};

const mapDispatchToProps = (dispatch: Dispatch<BannerActionTypes>) => ({
	handleGetDetail: (id: number) => {
		dispatch(actions.banner.detailRequest(id));
	},
	handleReloadAllData: () => {
		dispatch(actions.banner.getAllRequest());
	},
	handleSaveData: (data: BannerModel) => {
		dispatch(actions.banner.saveRequest(data));
	},
	handleDeleteData: (ids: number[]) => {
		dispatch(actions.banner.deleteRequest(ids));
	},
	onChangeSelectedIds: (ids: number[]) => {
		dispatch(actions.banner.changeSelectedIds(ids));
	},
	onShowDetail: (data: BaseShowDetail<BannerModel>) => {
		dispatch(actions.banner.showDetail(data));
	},
	onShowConfirm: (isShow: boolean) => {
		dispatch(actions.banner.showConfirm(isShow));
	},
});

type IBannerEditPageProps = {
	titlePage: string;
	status: eBaseActionStatus;
	allBanners: BannerModel[];
	selectedIds: number[];
	detailResponse?: BannerModel;
	showDetail: BaseShowDetail<BannerModel>;
	showConfirm: boolean;
	isSaving: boolean | false;
	handleReloadAllData: () => void;
	handleGetDetail: (id: number) => void;
	handleSaveData: (data: BannerModel) => void;
	handleDeleteData: (data: number[]) => void;
	onChangeSelectedIds: (ids: number[]) => void;
	onShowDetail: (data: BaseShowDetail<BannerModel>) => void;
	onShowConfirm: (isShow: boolean) => void;
};

const BannerEditPage = (props: IBannerEditPageProps) => {
	const { id } = useParams();
	const bannerId = useMemo(() => parseInt(id || '0'), [id]);
	const positionId = useMemo(() => parseInt(id || '0'), [id]);
	const listPageId = useMemo(() => parseInt(id || '0'), [id]);
	const navigate = useNavigate();

	const { status, detailResponse, handleGetDetail, handleReloadAllData, handleSaveData, isSaving } = props;

	const [initialValues, setInitialValues] = useState<BannerModel>(dfBannerModel);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

	const {
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<BannerModel>({
		resolver: yupResolver(validBannerSchema() as any),
		mode: 'all',
		defaultValues: initialValues,
	});

	const onSubmitHandler = (values: BannerModel) => {
		handleSaveData({ ...values });
		setHasUnsavedChanges(false);
	};

	useEffect(() => {
		handleReloadAllData();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (detailResponse) {
			setInitialValues((prev) => ({
				...prev,
				...detailResponse,
			}));
			setHasUnsavedChanges(false);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [detailResponse]);

	useEffect(() => {
		reset(initialValues);
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialValues]);

	useEffect(() => {
		if (bannerId > 0) {
			handleGetDetail(bannerId);
		} else {
			setInitialValues(dfBannerModel);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bannerId]);

	useEffect(() => {
		if (status === eBaseActionStatus.reload) navigate(history.location?.pathname || '/', { replace: true });
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status]);

	// Helper to update field and mark as unsaved
	const updateField = useCallback((field: keyof BannerModel, value: any) => {
		setInitialValues((prev) => ({ ...prev, [field]: value }));
		setHasUnsavedChanges(true);
	}, []);

	// Tab content: Basic Info
	const basicInfoTab = (
		<div className='space-y-6'>
			<EditPageSection
				title='Thông tin Banner'
				icon={<FileText className='h-4 w-4' />}
			>
				<div className='space-y-4'>
					<EditPageField
						label='Tên Banner'
						required
						error={errors.name?.message}
						helpText='Tên hiển thị của banner'
					>
						<TextBox
							name='name'
							value={initialValues.name}
							error={errors.name?.message}
							onValueChanged={(value) => updateField('name', value)}
							placeholder='Nhập tên banner...'
						/>
					</EditPageField>

					<EditPageField
						label='Tiêu đề'
						error={errors.title?.message}
					>
						<TextAreaBox
							name='title'
							value={initialValues.title}
							error={errors.title?.message}
							onValueChanged={(value) => updateField('title', value)}
							className='form-control-simple no-resize editor-focus editor-f-18'
							classWrapper='border-bottom'
							placeholder='Viết gì đó...'
						/>
					</EditPageField>

					<EditPageField
						label='Mô tả'
						error={errors.description?.message}
					>
						<TextAreaBox
							name='description'
							value={initialValues.description}
							onValueChanged={(value) => updateField('description', value)}
							className='form-control-simple no-resize editor-focus editor-f-16'
							classWrapper='border-bottom'
							placeholder='Viết gì đó...'
						/>
					</EditPageField>
				</div>
			</EditPageSection>

			<EditPageSection
				title='Hình ảnh Banner'
				icon={<ImageIcon className='h-4 w-4' />}
			>
				<ImageBox
					name='path'
					isDisabled={initialValues.name.length <= 0}
					file_name={initialValues.name}
					value={initialValues.path}
					onDataChanged={(image) => {
						updateField('path', image?.path || '');
						updateField('extension', image?.extension || '');
						updateField('size', image?.size || '');
					}}
				/>
			</EditPageSection>
		</div>
	);

	// Tab content: Display Settings
	const displaySettingsTab = (
		<div className='space-y-6'>
			<EditPageSection
				title='Kích thước Banner'
				icon={<Settings className='h-4 w-4' />}
			>
				<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
					<EditPageField
						label='Chiều rộng (px)'
						error={errors.width?.message}
					>
						<NumberBox
							name='width'
							value={initialValues.width}
							error={errors.width?.message}
							onValueChanged={(value) => updateField('width', value)}
							sizeClass='sm'
							isDisabled={true}
							helpBlock='Chiều rộng Banner'
						/>
					</EditPageField>

					<EditPageField
						label='Chiều cao (px)'
						error={errors.height?.message}
					>
						<NumberBox
							name='height'
							value={initialValues.height}
							error={errors.height?.message}
							onValueChanged={(value) => updateField('height', value)}
							sizeClass='sm'
							isDisabled={true}
							helpBlock='Chiều cao Banner'
						/>
					</EditPageField>
				</div>
			</EditPageSection>

			<EditPageSection
				title='Liên kết'
				icon={<LinkIcon className='h-4 w-4' />}
			>
				<EditPageField
					label='Đường dẫn liên kết'
					error={errors.link?.message}
					helpText='URL mà banner sẽ liên kết đến khi click'
				>
					<TextBox
						name='link'
						value={initialValues.link}
						error={errors.link?.message}
						onValueChanged={(value) => updateField('link', value)}
						placeholder='https://...'
					/>
				</EditPageField>
			</EditPageSection>

			<EditPageSection
				title='Thiết lập vị trí hiển thị'
				icon={<Settings className='h-4 w-4' />}
			>
				<BannerConfigTable bannerId={bannerId} positionId={positionId} listPageId={listPageId} />
			</EditPageSection>
		</div>
	);

	// Tab content: Schedule
	const scheduleTab = (
		<div className='space-y-6'>
			<EditPageSection
				title='Lịch hiển thị'
				icon={<Calendar className='h-4 w-4' />}
			>
				<div className='space-y-4'>
					<EditPageField
						label='Ngày bắt đầu'
						helpText='Banner sẽ bắt đầu hiển thị từ ngày này'
					>
						<DxDateBox
							name='beginDate'
							value={moment(initialValues.beginDate).format()}
							displayFormat={'dd/MM/yyyy HH:mm'}
							typeDate='datetime'
							onValueChanged={(e: any) => updateField('beginDate', e.value)}
						/>
					</EditPageField>

					<EditPageField
						label='Ngày kết thúc'
						helpText='Banner sẽ ngừng hiển thị từ ngày này'
					>
						<DxDateBox
							name='endDate'
							value={moment(initialValues.endDate).format()}
							displayFormat={'dd/MM/yyyy HH:mm'}
							typeDate='datetime'
							onValueChanged={(e: any) => updateField('endDate', e.value)}
						/>
					</EditPageField>
				</div>
			</EditPageSection>

			<EditPageSection
				title='Trạng thái'
				icon={<Settings className='h-4 w-4' />}
			>
				<EditPageField
					label='Trạng thái hoạt động'
				>
					<SwitchBox
						name='inActive'
						label='Áp dụng banner này'
						error={errors.inActive?.message}
						onValueChanged={(value) => updateField('inActive', value)}
						value={initialValues.inActive}
						sizeClass='sm'
					/>
				</EditPageField>
			</EditPageSection>
		</div>
	);

	const tabs = [
		{
			id: 'basic',
			label: 'Thông tin cơ bản',
			icon: <FileText className='h-4 w-4' />,
			content: basicInfoTab,
		},
		{
			id: 'display',
			label: 'Cài đặt hiển thị',
			icon: <Settings className='h-4 w-4' />,
			content: displaySettingsTab,
		},
		{
			id: 'schedule',
			label: 'Lịch & Trạng thái',
			icon: <Calendar className='h-4 w-4' />,
			content: scheduleTab,
		},
	];

	// Action buttons
	const actionButtons = (
		<Button
			theme='primary'
			preset='default'
			onClick={handleSubmit(onSubmitHandler as any)}
			isLoading={isSaving}
			isDisabled={isSaving}
			text='Lưu thay đổi'
			sizeClass='md'
			icon='icon ni ni-check-thick'
		/>
	);

	return (
		<EditPageLayout
			title='Thông tin Banner'
			description={`${bannerId > 0 ? 'Chỉnh sửa' : 'Tạo mới'} banner`}
			tabs={tabs}
			actions={actionButtons}
			isSaving={isSaving}
			onSave={handleSubmit(onSubmitHandler as any)}
			onCancel={() => navigate(-1)}
			hasUnsavedChanges={hasUnsavedChanges}
			breadcrumbs={[
				{ label: 'Trang chủ', href: '/' },
				{ label: 'Banner', href: '/ads-items' },
				{ label: bannerId > 0 ? 'Chỉnh sửa' : 'Tạo mới' },
			]}
		/>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(BannerEditPage);
